// @file Landside navigation store
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { OzBaseButtonHrefMode } from 'library/v4/components/OzBaseButton.vue'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum LandsidePage {
  Home = 'home',
  Product = 'product',
  Subscriptions = 'subscriptions',
  Help = 'help',
  Careers = 'careers',
}

export const useLandsideNavigationStore = defineStore('landsideNavigation', () => {
  // State
  const currentPage = ref(LandsidePage.Home)
  const isUserLoggedIn = ref(false)

  // Getters
  const landsidePages = computed(
    (): Array<{
      key: LandsidePage
      text: string
      href: string
      hrefMode: OzBaseButtonHrefMode
    }> => [
      {
        key: LandsidePage.Home,
        text: __('Home'),
        href: '/',
        hrefMode: isUserLoggedIn.value ? OzBaseButtonHrefMode.Default : OzBaseButtonHrefMode.HistoryPushState,
      },
      {
        key: LandsidePage.Product,
        text: __('Product'),
        href: '/site/product',
        hrefMode: OzBaseButtonHrefMode.HistoryPushState,
      },
      {
        key: LandsidePage.Subscriptions,
        text: __('Subscriptions'),
        // TODO: Replace href with #subscriptions when subscriptions page is done
        href: 'https://padlet.com/memberships',
        hrefMode: OzBaseButtonHrefMode.Default,
      },
      {
        key: LandsidePage.Help,
        text: __('Help'),
        // TODO: Replace href with #help when memberships page is done
        href: 'https://padlet.help',
        hrefMode: OzBaseButtonHrefMode.Default,
      },
      {
        key: LandsidePage.Careers,
        text: __('Careers'),
        // TODO: Replace href with #careers when memberships page is done
        href: 'https://padlet.jobs',
        hrefMode: OzBaseButtonHrefMode.Default,
      },
    ],
  )

  // Actions

  function initializeState(data: { currentPage: LandsidePage; isUserLoggedIn: boolean }): void {
    currentPage.value = data.currentPage ?? currentPage.value
    isUserLoggedIn.value = data.isUserLoggedIn ?? isUserLoggedIn.value
  }

  function setCurrentPage(page: LandsidePage): void {
    // If user is logged in, they should be directed to /dashboard and we want to prevent the UI from changing
    if (page === LandsidePage.Home && isUserLoggedIn.value) return
    if (!(page === LandsidePage.Home || page === LandsidePage.Product)) return
    currentPage.value = page
  }

  function switchPageToUrlParams(): void {
    const page = window.location.pathname.replace(/(\/site|(\/))/g, '')

    setCurrentPage(page === '' ? LandsidePage.Home : (page as LandsidePage))
  }

  return {
    // State
    currentPage,
    isUserLoggedIn,

    // Getters
    landsidePages,

    // Actions
    initializeState,
    setCurrentPage,
    switchPageToUrlParams,
  }
})
